import { Head, Link } from '@inertiajs/react';
import { type CSSProperties, type ReactElement } from 'react';

import {
    Btn,
    Chip,
    DesignFooter,
    DesignHeader,
    Headshot,
    I,
    LawyerCardV,
    PhoneCTA,
    SectionHeader,
    Stars,
    TrustBadge,
    UtilityBar,
    type LawyerCardData,
} from '@/Components/Design';
import { type ServerLawyerCard } from '@/Support/toCardData';

type PracticeAreaTile = { slug: string; name: string; icon: string; lawyersCount?: number };
type StateTile = { slug: string; name: string; abbreviation: string; lawyersCount: number };

type Props = {
    featured: ServerLawyerCard[];
    practiceAreas: PracticeAreaTile[];
    topStates: StateTile[];
};

/* Practice-area icon set — maps Phase 6 slugs (car-accidents, etc.)
 * to the design icon vocabulary. */
const PA_ICON: Record<string, ReactElement> = {
    'car-accidents': I.car,
    'truck-accidents': I.truck,
    'motorcycle-accidents': I.bike,
    'slip-and-fall': I.fall,
    'medical-malpractice': I.med,
    'workers-compensation': I.work,
    'wrongful-death': I.death,
    'product-liability': I.product,
    'dog-bites': I.dog,
    'nursing-home-abuse': I.shield,
    'construction-accidents': I.work,
    'brain-and-spinal-injuries': I.med,
};

const PA_SUBTITLE: Record<string, string> = {
    'car-accidents': 'Including hit-and-run, uninsured motorist',
    'truck-accidents': 'Commercial trucks, 18-wheelers',
    'motorcycle-accidents': 'Including bicycle & scooter',
    'slip-and-fall': 'Premises liability, public & private',
    'medical-malpractice': 'Misdiagnosis, surgical, birth injury',
    'workers-compensation': 'Including third-party injury claims',
    'wrongful-death': 'For surviving family members',
    'product-liability': 'Defective products, recalls',
    'dog-bites': "Including children's injuries",
    'nursing-home-abuse': 'Negligence, abuse, neglect',
    'construction-accidents': 'On-site injuries, OSHA violations',
    'brain-and-spinal-injuries': 'TBI, paralysis, long-term care',
};

/* Hero search box — visual stub for now; full form ships in a follow-up. */
function HeroSearch({ practiceAreas }: { practiceAreas: PracticeAreaTile[] }) {
    return (
        <div
            data-hero-search-card
            style={{
                background: 'var(--card)',
                border: '1px solid var(--rule)',
                borderRadius: 16,
                padding: 18,
                boxShadow: 'var(--shadow-card)',
            }}
        >
            <div data-hero-search-grid style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.4fr auto', gap: 10, alignItems: 'stretch' }}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        padding: '12px 16px',
                        border: '1px solid var(--rule)',
                        borderRadius: 10,
                        background: 'var(--paper)',
                    }}
                >
                    <div
                        style={{
                            fontSize: 11,
                            color: 'var(--ink-mute)',
                            letterSpacing: '.06em',
                            textTransform: 'uppercase',
                            fontWeight: 500,
                        }}
                    >
                        What happened
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 15, color: 'var(--ink)', fontWeight: 500 }}>Car accident</span>
                        <span style={{ display: 'inline-flex', color: 'var(--ink-mute)' }}>{I.chevron}</span>
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        padding: '12px 16px',
                        border: '1px solid var(--rule)',
                        borderRadius: 10,
                        background: 'var(--paper)',
                    }}
                >
                    <div
                        style={{
                            fontSize: 11,
                            color: 'var(--ink-mute)',
                            letterSpacing: '.06em',
                            textTransform: 'uppercase',
                            fontWeight: 500,
                        }}
                    >
                        Where
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 15, color: 'var(--ink)', fontWeight: 500 }}>Your city</span>
                        <button
                            type="button"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                border: 0,
                                background: 'transparent',
                                color: 'var(--ember)',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            <span style={{ display: 'inline-flex' }}>{I.pin}</span> Use my location
                        </button>
                    </div>
                </div>
                <Btn kind="primary" size="xl" icon={I.search} href="/search">
                    Find lawyers
                </Btn>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14 }}>
                {practiceAreas.slice(0, 7).map((p, i) => (
                    <Chip key={p.slug} icon={PA_ICON[p.slug]} label={p.name} selected={i === 0} />
                ))}
                <Chip label="More →" tone="soft" />
            </div>
        </div>
    );
}

const HOW_IT_WORKS: { n: string; t: string; b: string }[] = [
    {
        n: '01',
        t: 'Tell us what happened',
        b: "30 seconds. One question per screen. Plain English. You don't need to know your case type — we'll figure it out.",
    },
    {
        n: '02',
        t: 'We match you to local attorneys',
        b: 'Up to three lawyers within driving distance, all bar-verified, taking new cases this week.',
    },
    {
        n: '03',
        t: 'Talk free — no obligation',
        b: 'Whichever you click — phone, form, text — your information goes straight to the attorney, not a call center.',
    },
];

const REVIEWS: { q: string; n: string; c: string; r: number }[] = [
    {
        q: 'Found a lawyer within twenty minutes of leaving the ER. He picked up himself, on the first ring. That alone was different from every other site I tried.',
        n: 'D. Alvarez',
        c: 'Phoenix, AZ · Car accident',
        r: 5,
    },
    {
        q: "The Spanish version of the matching flow actually worked — wasn't half-translated like Avvo. Mi mamá la pudo usar sola.",
        n: 'L. Ortega',
        c: 'San Diego, CA · Slip & fall',
        r: 5,
    },
    {
        q: 'I was very skeptical of any lawyer site after the billboard people kept calling me. This one… didn\'t do that. Just gave me three names and let me pick.',
        n: 'J. Whitman',
        c: 'Nashville, TN · Workers\' comp',
        r: 5,
    },
];

const FAQ: { q: string; a: string; open?: boolean }[] = [
    { q: 'Is this service really free?', a: 'Yes. We never charge you. Attorneys pay to be listed; that pays for the site.', open: true },
    { q: 'Do you screen the attorneys?', a: "Every attorney is verified against their state bar's active license database. We re-check monthly." },
    { q: "What if I can't afford a lawyer?", a: 'Almost every personal injury lawyer works on contingency — they only get paid if they win money for you.' },
    { q: 'How fast will an attorney contact me?', a: "Most respond within an hour. The matched attorneys you'll see have agreed to respond same-day." },
    { q: 'Do I have to use the lawyer you match me with?', a: 'No. We send you three, you pick zero, one, or talk to all three. Free consultations come with no obligation.' },
    { q: "What's the deadline to file my case?", a: 'Depends entirely on the state and case type. Some states have one-year limits. Talk to a lawyer this week.' },
];

const MAP_GRID: (string | null)[][] = [
    ['WA', 'OR', 'ID', 'MT', 'ND', 'MN', 'WI', 'MI', 'NY', 'VT', 'ME'],
    ['CA', 'NV', 'UT', 'WY', 'SD', 'IA', 'IL', 'IN', 'OH', 'PA', 'NH'],
    [null, 'AZ', 'NM', 'CO', 'NE', 'MO', 'KY', 'WV', 'VA', 'NJ', 'MA'],
    [null, null, null, 'OK', 'KS', 'AR', 'TN', 'NC', 'DC', 'DE', 'CT'],
    [null, null, null, 'TX', 'LA', 'MS', 'AL', 'GA', 'SC', 'MD', 'RI'],
    ['HI', null, null, null, null, null, null, 'FL', null, 'AK', null],
];

const FEATURED_STATES_SET = new Set(['CA', 'TX', 'FL', 'NY', 'IL', 'PA']);

const STATE_ABBR_TO_SLUG: Record<string, string> = {
    AL: 'alabama', AK: 'alaska', AZ: 'arizona', AR: 'arkansas', CA: 'california',
    CO: 'colorado', CT: 'connecticut', DE: 'delaware', DC: 'district-of-columbia',
    FL: 'florida', GA: 'georgia', HI: 'hawaii', ID: 'idaho', IL: 'illinois',
    IN: 'indiana', IA: 'iowa', KS: 'kansas', KY: 'kentucky', LA: 'louisiana',
    ME: 'maine', MD: 'maryland', MA: 'massachusetts', MI: 'michigan', MN: 'minnesota',
    MS: 'mississippi', MO: 'missouri', MT: 'montana', NE: 'nebraska', NV: 'nevada',
    NH: 'new-hampshire', NJ: 'new-jersey', NM: 'new-mexico', NY: 'new-york',
    NC: 'north-carolina', ND: 'north-dakota', OH: 'ohio', OK: 'oklahoma',
    OR: 'oregon', PA: 'pennsylvania', RI: 'rhode-island', SC: 'south-carolina',
    SD: 'south-dakota', TN: 'tennessee', TX: 'texas', UT: 'utah', VT: 'vermont',
    VA: 'virginia', WA: 'washington', WV: 'west-virginia', WI: 'wisconsin', WY: 'wyoming',
};

function toLawyerCardData(s: ServerLawyerCard, fallbackHue: number): LawyerCardData {
    const parts = s.name.trim().split(/\s+/).filter(Boolean);
    const initials = (((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase() || 'AN');
    return {
        name: s.name,
        firm: s.firm ?? '',
        city: s.city ?? '',
        state: s.stateAbbr ?? '',
        rating: typeof s.reviewsAvg === 'number' ? s.reviewsAvg : 0,
        reviews: s.reviewsCount ?? 0,
        years: typeof s.yearsLicensed === 'number' ? s.yearsLicensed : 0,
        areas: s.practiceAreas.map((p) => p.name),
        langs: s.languages,
        initials,
        hue: fallbackHue,
        featured: s.featured,
        href: `/${s.slug}`,
        headshotUrl: s.headshotKey ? `/cdn/lawyers/320/${s.headshotKey}.webp` : null,
    };
}

const SECTION: CSSProperties = { padding: '72px 56px', maxWidth: 1440, margin: '0 auto' };
const SECTION_TIGHT: CSSProperties = { padding: '56px 56px', maxWidth: 1440, margin: '0 auto' };

export default function Home({ featured, practiceAreas, topStates }: Props) {
    return (
        <>
            <Head>
                <title>injury.help — Find a personal injury lawyer who's actually local</title>
            </Head>
            <div style={{ background: 'var(--paper)', minHeight: '100vh' }}>
                <UtilityBar />
                <DesignHeader active="Find a Lawyer" />

                {/* Hero */}
                <section data-home-section="hero" style={{ padding: '96px 56px 80px', maxWidth: 1440, margin: '0 auto' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.05fr .95fr', gap: 64, alignItems: 'center' }}>
                        <div>
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '5px 10px',
                                    border: '1px solid var(--rule)',
                                    borderRadius: 999,
                                    fontSize: 12,
                                    color: 'var(--ink-soft)',
                                    marginBottom: 24,
                                    background: 'var(--card)',
                                }}
                            >
                                <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--sage)' }} />
                                13,994 verified attorneys across all 50 states
                            </div>
                            <h1 style={{ fontSize: 80, lineHeight: 1.02, letterSpacing: '-0.025em', marginBottom: 24, fontFamily: 'var(--font-serif)', fontWeight: 500 }}>
                                Find a personal injury lawyer who's <em>actually local</em> — and actually returns calls.
                            </h1>
                            <p
                                style={{
                                    fontSize: 20,
                                    color: 'var(--ink-soft)',
                                    lineHeight: 1.5,
                                    maxWidth: 560,
                                    marginBottom: 36,
                                }}
                            >
                                A bar-verified directory of personal injury attorneys across all 50 states — without the billboard noise. Search by location or case type, and contact the firm directly.
                            </p>
                            <div data-hero-search>
                                <HeroSearch practiceAreas={practiceAreas} />
                            </div>
                            <div data-trust-row style={{ marginTop: 20, display: 'flex', gap: 24, alignItems: 'center', color: 'var(--ink-soft)', fontSize: 13 }}>
                                <TrustBadge tone="sage" icon={I.check} label="State Bar verified" />
                                <TrustBadge tone="ink" icon={I.shield} label="13,994+ attorneys" />
                                <TrustBadge tone="ember" icon={I.dollar} label="Always free for you" />
                            </div>
                        </div>
                        <div style={{ position: 'relative' }}>
                            <div
                                style={{
                                    background: 'var(--card)',
                                    border: '1px solid var(--rule)',
                                    borderRadius: 16,
                                    padding: 32,
                                    boxShadow: 'var(--shadow-card)',
                                    position: 'relative',
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 11,
                                        letterSpacing: '.12em',
                                        textTransform: 'uppercase',
                                        color: 'var(--ember)',
                                        marginBottom: 12,
                                        fontWeight: 500,
                                    }}
                                >
                                    Or, talk to a real person
                                </div>
                                <div
                                    style={{
                                        fontFamily: 'var(--font-serif)',
                                        fontSize: 32,
                                        lineHeight: 1.2,
                                        letterSpacing: '-0.015em',
                                        marginBottom: 18,
                                    }}
                                >
                                    "Tell us what happened. We'll match you with three local attorneys in under an hour."
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                                    <Headshot initials="MR" hue={2} size={42} radius={999} />
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 500 }}>Maria R. — Intake counsel</div>
                                        <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>Bilingual · 24/7 · Free</div>
                                    </div>
                                </div>
                                <PhoneCTA number="1-833-INJURY-1" size="lg" full />
                                <div style={{ marginTop: 14, fontSize: 12, color: 'var(--ink-mute)', textAlign: 'center' }}>
                                    Average response time today: <b style={{ color: 'var(--ink)' }}>38 minutes</b>
                                </div>
                            </div>
                            <div
                                data-hero-side-shape
                                style={{
                                    position: 'absolute',
                                    right: -24,
                                    top: -24,
                                    width: 72,
                                    height: 72,
                                    borderRadius: 999,
                                    background: 'var(--ember-tint)',
                                    zIndex: -1,
                                }}
                            />
                            <div
                                data-hero-side-shape
                                style={{
                                    position: 'absolute',
                                    left: -32,
                                    bottom: -32,
                                    width: 120,
                                    height: 120,
                                    borderRadius: 999,
                                    border: '1px solid var(--rule)',
                                    zIndex: -1,
                                }}
                            />
                        </div>
                    </div>
                </section>

                {/* How it works */}
                <section data-home-section="howitworks" style={{ ...SECTION_TIGHT, borderTop: '1px solid var(--rule)' }}>
                    <SectionHeader
                        eyebrow="How it works"
                        title="Three steps. About a minute."
                        sub="Designed for someone in a hospital bed, not a quiet office."
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
                        {HOW_IT_WORKS.map((s) => (
                            <div
                                key={s.n}
                                data-card
                                style={{
                                    background: 'var(--card)',
                                    border: '1px solid var(--rule)',
                                    borderRadius: 16,
                                    padding: 32,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 12,
                                    minHeight: 240,
                                }}
                            >
                                <div className="pip-mono" style={{ fontSize: 13, color: 'var(--ember)', fontWeight: 600 }}>
                                    {s.n}
                                </div>
                                <h3 style={{ fontSize: 24, lineHeight: 1.2 }}>{s.t}</h3>
                                <p style={{ fontSize: 15, color: 'var(--ink-soft)', lineHeight: 1.55 }}>{s.b}</p>
                                <svg width="100%" height="56" viewBox="0 0 200 56" fill="none" style={{ marginTop: 'auto' }}>
                                    {s.n === '01' && (
                                        <>
                                            <rect x="20" y="14" width="160" height="28" rx="6" stroke="var(--ink)" strokeWidth="1.5" />
                                            <path d="M30 28 L70 28" stroke="var(--ember)" strokeWidth="1.5" />
                                            <circle cx="160" cy="28" r="3" fill="var(--ember)" />
                                        </>
                                    )}
                                    {s.n === '02' && (
                                        <>
                                            <circle cx="40" cy="28" r="10" stroke="var(--ink)" strokeWidth="1.5" />
                                            <circle cx="100" cy="28" r="10" stroke="var(--ink)" strokeWidth="1.5" />
                                            <circle cx="160" cy="28" r="10" stroke="var(--ember)" strokeWidth="1.5" fill="var(--ember-tint)" />
                                            <path d="M50 28 L90 28 M110 28 L150 28" stroke="var(--ink)" strokeWidth="1.5" strokeDasharray="2 3" />
                                        </>
                                    )}
                                    {s.n === '03' && (
                                        <>
                                            <path d="M20 28 Q 100 8, 180 28" stroke="var(--ink)" strokeWidth="1.5" fill="none" />
                                            <circle cx="20" cy="28" r="4" fill="var(--ink)" />
                                            <path d="M170 22 L180 28 L170 34" stroke="var(--ember)" strokeWidth="1.5" fill="none" />
                                        </>
                                    )}
                                </svg>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Practice areas */}
                <section data-home-section="practiceareas" style={SECTION}>
                    <SectionHeader
                        eyebrow="Browse by case type"
                        title="What happened to you?"
                        action={<Btn kind="text" href="/search">All practice areas →</Btn>}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
                        {practiceAreas.map((p) => (
                            <Link
                                key={p.slug}
                                href={`/${p.slug}`}
                                style={{
                                    background: 'var(--card)',
                                    border: '1px solid var(--rule)',
                                    borderRadius: 12,
                                    padding: 20,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 8,
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    transition: 'all var(--dur)',
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div
                                        style={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: 8,
                                            background: 'var(--paper-deep)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'var(--ink)',
                                        }}
                                    >
                                        {PA_ICON[p.slug] ?? I.scales}
                                    </div>
                                    {typeof p.lawyersCount === 'number' && p.lawyersCount > 0 && (
                                        <div className="pip-mono" style={{ fontSize: 11, color: 'var(--ink-mute)' }}>
                                            {p.lawyersCount.toLocaleString()}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <div style={{ fontFamily: 'var(--font-serif)', fontSize: 17, fontWeight: 500, lineHeight: 1.2 }}>
                                        {p.name}
                                    </div>
                                    <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginTop: 4, lineHeight: 1.4 }}>
                                        {PA_SUBTITLE[p.slug] ?? 'Click to browse attorneys'}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>

                {/* Browse by state — stylized US map */}
                <section data-home-section="statemap" style={SECTION}>
                    <SectionHeader
                        eyebrow="By location"
                        title="Browse 13,994 attorneys across all 50 states."
                        sub="A clickable map. Tab key works. Each state opens a hub with cities, statute-of-limitations notes, and a filtered directory."
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 32, alignItems: 'stretch' }}>
                        <div data-card style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 16, padding: 32, position: 'relative' }}>
                            <svg viewBox="0 0 800 480" style={{ width: '100%', height: 'auto' }}>
                                {MAP_GRID.map((row, r) =>
                                    row.map((s, c) => {
                                        if (!s) return null;
                                        const x = 70 + c * 65;
                                        const y = 60 + r * 70;
                                        const featuredState = FEATURED_STATES_SET.has(s);
                                        const slug = STATE_ABBR_TO_SLUG[s];
                                        return (
                                            <a key={`${r}-${c}-${s}`} href={slug ? `/${slug}` : '#'} style={{ cursor: 'pointer' }}>
                                                <rect
                                                    x={x}
                                                    y={y}
                                                    width="56"
                                                    height="62"
                                                    rx="6"
                                                    fill={featuredState ? 'var(--ember-tint)' : 'var(--paper-deep)'}
                                                    stroke={featuredState ? 'var(--ember)' : 'var(--rule)'}
                                                    strokeWidth="1.2"
                                                />
                                                <text
                                                    x={x + 28}
                                                    y={y + 35}
                                                    textAnchor="middle"
                                                    style={{
                                                        fontFamily: 'var(--font-sans)',
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                        fill: featuredState ? 'var(--ember-deep)' : 'var(--ink)',
                                                    }}
                                                >
                                                    {s}
                                                </text>
                                            </a>
                                        );
                                    }),
                                )}
                            </svg>
                            <div
                                style={{
                                    position: 'absolute',
                                    left: 32,
                                    bottom: 32,
                                    display: 'flex',
                                    gap: 14,
                                    fontSize: 12,
                                    color: 'var(--ink-mute)',
                                }}
                            >
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ width: 10, height: 10, background: 'var(--ember-tint)', border: '1px solid var(--ember)', borderRadius: 3 }} />
                                    500+ attorneys
                                </span>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ width: 10, height: 10, background: 'var(--paper-deep)', border: '1px solid var(--rule)', borderRadius: 3 }} />
                                    Active
                                </span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div
                                style={{
                                    fontSize: 11,
                                    letterSpacing: '.12em',
                                    textTransform: 'uppercase',
                                    color: 'var(--ink-mute)',
                                    fontWeight: 500,
                                    marginBottom: 4,
                                }}
                            >
                                Most active this week
                            </div>
                            {topStates.slice(0, 6).map((s) => (
                                <Link
                                    key={s.slug}
                                    href={`/${s.slug}`}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '14px 18px',
                                        border: '1px solid var(--rule)',
                                        borderRadius: 12,
                                        background: 'var(--card)',
                                        textDecoration: 'none',
                                        color: 'inherit',
                                    }}
                                >
                                    <div>
                                        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 500 }}>{s.name}</div>
                                        <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginTop: 2 }}>{s.abbreviation}</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div className="pip-mono" style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 600 }}>
                                            {s.lawyersCount.toLocaleString()}
                                        </div>
                                        <div style={{ fontSize: 11, color: 'var(--ink-mute)' }}>attorneys</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Featured attorneys */}
                {featured.length > 0 && (
                    <section data-home-section="featured" style={{ padding: '56px 56px 72px', maxWidth: 1440, margin: '0 auto' }}>
                        <SectionHeader
                            eyebrow="Recently active"
                            title="A few attorneys taking new cases"
                            sub="Vetted, verified, responding within the hour today."
                            action={<Btn kind="text" href="/search">See all →</Btn>}
                        />
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
                            {featured.slice(0, 3).map((l, i) => (
                                <LawyerCardV key={l.id} data={toLawyerCardData(l, i)} />
                            ))}
                        </div>
                    </section>
                )}

                {/* Editorial block */}
                <section data-home-section="editorial" style={{ padding: '72px 56px', background: 'var(--paper-deep)', borderTop: '1px solid var(--rule)' }}>
                    <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 64, alignItems: 'flex-start' }}>
                        <div>
                            <div
                                style={{
                                    fontSize: 11,
                                    letterSpacing: '.12em',
                                    textTransform: 'uppercase',
                                    color: 'var(--ember)',
                                    marginBottom: 8,
                                    fontWeight: 500,
                                }}
                            >
                                Plain English
                            </div>
                            <h2 style={{ fontSize: 44, lineHeight: 1.1, letterSpacing: '-0.02em' }}>What is personal injury law, really?</h2>
                        </div>
                        <div data-columns style={{ columnCount: 2, columnGap: 32, fontSize: 16, lineHeight: 1.7, color: 'var(--ink-soft)' }}>
                            <p style={{ marginBottom: 14 }}>
                                Personal injury law is the part of the legal system that gets you paid back when someone else's carelessness hurts you — physically, financially, or both. A car runs a red light. A grocery store leaves a wet floor unmarked. A doctor misses an obvious diagnosis. You end up in the ER, miss two weeks of work, and watch the bills pile up.
                            </p>
                            <p style={{ marginBottom: 14 }}>
                                You don't need to know the legal name for what happened. You don't need to have proof. You don't even need to be sure it was someone else's fault. A lawyer's job — at the first call, free — is to figure that out for you.
                            </p>
                            <p style={{ marginBottom: 14 }}>
                                Nearly every personal injury attorney works on what's called <em>contingency</em>: they only get paid if they win money for you. If they don't, you owe nothing. Read that twice, because it's the most important sentence on this page.
                            </p>
                            <p>
                                <Link href="/resources" style={{ color: 'var(--ember)', borderBottom: '1px solid var(--ember)', textDecoration: 'none' }}>
                                    Read the full guide →
                                </Link>
                            </p>
                        </div>
                    </div>
                </section>

                {/* Trust + FAQ */}
                <section data-home-section="trust-faq" style={SECTION}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 64 }}>
                        <div>
                            <div
                                style={{
                                    fontSize: 11,
                                    letterSpacing: '.12em',
                                    textTransform: 'uppercase',
                                    color: 'var(--ember)',
                                    marginBottom: 8,
                                    fontWeight: 500,
                                }}
                            >
                                From real users
                            </div>
                            <h2 style={{ fontSize: 36, lineHeight: 1.15, marginBottom: 32 }}>Quiet, useful, and not predatory.</h2>
                            {REVIEWS.map((r, i) => (
                                <div key={i} style={{ padding: '20px 0', borderTop: '1px solid var(--rule)' }}>
                                    <Stars value={r.r} />
                                    <p style={{ fontFamily: 'var(--font-serif)', fontSize: 18, lineHeight: 1.4, margin: '10px 0', color: 'var(--ink)' }}>
                                        "{r.q}"
                                    </p>
                                    <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>{r.n} · {r.c}</div>
                                </div>
                            ))}
                        </div>
                        <div>
                            <div
                                style={{
                                    fontSize: 11,
                                    letterSpacing: '.12em',
                                    textTransform: 'uppercase',
                                    color: 'var(--ember)',
                                    marginBottom: 8,
                                    fontWeight: 500,
                                }}
                            >
                                Common questions
                            </div>
                            <h2 style={{ fontSize: 36, lineHeight: 1.15, marginBottom: 24 }}>Things people ask in the first call.</h2>
                            {FAQ.map((f, i) => (
                                <details key={i} open={f.open} style={{ borderBottom: '1px solid var(--rule)', padding: '18px 0' }}>
                                    <summary
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            gap: 24,
                                            cursor: 'pointer',
                                            listStyle: 'none',
                                        }}
                                    >
                                        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 19, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.3 }}>
                                            {f.q}
                                        </div>
                                        <span style={{ fontSize: 18, color: 'var(--ink-soft)', lineHeight: 1, marginTop: 2 }}>+</span>
                                    </summary>
                                    <p style={{ fontSize: 15, color: 'var(--ink-soft)', lineHeight: 1.6, marginTop: 10 }}>{f.a}</p>
                                </details>
                            ))}
                        </div>
                    </div>
                </section>

                {/* For attorneys CTA bar */}
                <section data-home-section="for-attorneys" style={{ padding: '56px 56px', background: 'var(--ink)', color: 'var(--paper)' }}>
                    <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 32 }}>
                        <div>
                            <div
                                style={{
                                    fontSize: 11,
                                    letterSpacing: '.12em',
                                    textTransform: 'uppercase',
                                    color: 'var(--ember)',
                                    marginBottom: 8,
                                    fontWeight: 500,
                                }}
                            >
                                For attorneys
                            </div>
                            <h2 style={{ fontSize: 36, lineHeight: 1.15, color: 'var(--paper)', marginBottom: 8 }}>
                                Are you an injury attorney? Claim your free profile.
                            </h2>
                            <p style={{ fontSize: 16, color: 'rgba(247,243,236,.7)', maxWidth: 600 }}>
                                No upfront cost. Add your bio, practice areas, and direct line. Upgraded listings get priority placement in matched results.
                            </p>
                        </div>
                        <Btn kind="primary" size="lg" icon={I.arrow} href="/for-attorneys">
                            Claim profile
                        </Btn>
                    </div>
                </section>

                <DesignFooter />
            </div>
        </>
    );
}
